/**
 * RunAI OS Layout — AI Operating System shell
 * Route: /dashboard
 * Dock: bottom/top/left/right (sağ tıkla değiştir)
 * OS dark/light override'lari sadece bu layout aktifken uygulanir
 */
import { useEffect } from 'react'
import {
    ChatBar, Desktop, Dock, MenuBar,
    NotificationProvider, NotificationToast,
    OSThemeProvider,
    WindowManagerProvider,
} from '../os'
import { useDockPosition } from '../os/Dock/useDockPosition'
import { useOSTheme } from '../os/OSThemeContext'

// Site CSS variables'larini OS dark/light degerlerine gore override eder
// Sadece /dashboard route'unda aktif — cikinca temizlenir
function useOSCSSOverride() {
  const { isDark } = useOSTheme()

  useEffect(() => {
    const r = document.documentElement
    if (isDark) {
      r.style.setProperty('--body-bg', '#0d1117')
      r.style.setProperty('--card-bg', 'rgba(22,27,34,0.9)')
      r.style.setProperty('--bg-secondary', 'rgba(13,17,23,0.8)')
      r.style.setProperty('--bg-primary', 'rgba(22,27,34,0.9)')
      r.style.setProperty('--surface', 'rgba(22,27,34,0.88)')
      r.style.setProperty('--input-bg', 'rgba(255,255,255,0.04)')
      r.style.setProperty('--border-color', 'rgba(255,255,255,0.07)')
      r.style.setProperty('--border-default', 'rgba(255,255,255,0.07)')
      r.style.setProperty('--border', 'rgba(255,255,255,0.07)')
      r.style.setProperty('--text-primary', '#e6edf3')
      r.style.setProperty('--text-secondary', '#8b949e')
      r.style.setProperty('--text-muted', '#484f58')
      r.style.setProperty('--text-tertiary', '#484f58')
      r.style.setProperty('--hover-bg', 'rgba(255,255,255,0.06)')
    } else {
      r.style.setProperty('--body-bg', '#eef0f5')
      r.style.setProperty('--card-bg', '#ffffff')
      r.style.setProperty('--bg-secondary', '#f4f5f8')
      r.style.setProperty('--bg-primary', '#ffffff')
      r.style.setProperty('--surface', '#ffffff')
      r.style.setProperty('--input-bg', '#f4f5f8')
      r.style.setProperty('--border-color', 'rgba(0,0,0,0.09)')
      r.style.setProperty('--border-default', 'rgba(0,0,0,0.09)')
      r.style.setProperty('--border', 'rgba(0,0,0,0.09)')
      r.style.setProperty('--text-primary', '#1a1d23')
      r.style.setProperty('--text-secondary', '#6b7280')
      r.style.setProperty('--text-muted', '#9ca3af')
      r.style.setProperty('--text-tertiary', '#9ca3af')
      r.style.setProperty('--hover-bg', 'rgba(0,0,0,0.05)')
    }

    // Cleanup: /dashboard'dan cikinca override'lari temizle
    return () => {
      r.style.removeProperty('--body-bg')
      r.style.removeProperty('--card-bg')
      r.style.removeProperty('--bg-secondary')
      r.style.removeProperty('--bg-primary')
      r.style.removeProperty('--surface')
      r.style.removeProperty('--input-bg')
      r.style.removeProperty('--border-color')
      r.style.removeProperty('--border-default')
      r.style.removeProperty('--border')
      r.style.removeProperty('--text-primary')
      r.style.removeProperty('--text-secondary')
      r.style.removeProperty('--text-muted')
      r.style.removeProperty('--text-tertiary')
      r.style.removeProperty('--hover-bg')
    }
  }, [isDark])
}

function OSShell() {
  const { isDark } = useOSTheme()
  const { position } = useDockPosition()
  useOSCSSOverride()

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      overflow: 'hidden',
      background: 'var(--os-desktop-bg)',
      color: 'var(--os-text)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", sans-serif',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      position: 'relative',
      transition: 'background 0.3s',
    }}>
      {isDark && (
        <div style={{
          position: 'fixed', top: '-50%', left: '-50%', width: '200%', height: '200%',
          background: 'radial-gradient(circle at 30% 30%,rgba(99,102,241,.07),transparent 40%),radial-gradient(circle at 70% 70%,rgba(236,72,153,.05),transparent 45%)',
          zIndex: 0, pointerEvents: 'none',
        }} />
      )}

      {/* MenuBar always top */}
      <MenuBar />

      {/* Top dock */}
      {position === 'top' && <Dock />}

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Left dock */}
        {position === 'left' && <Dock />}

        {/* Desktop */}
        <Desktop />

        {/* Right dock */}
        {position === 'right' && <Dock />}
      </div>

      {/* ChatBar */}
      <ChatBar />

      {/* Bottom dock (default) */}
      {position === 'bottom' && <Dock />}

      <NotificationToast />
    </div>
  )
}

export default function RunAIOSLayout() {
  return (
    <OSThemeProvider>
      <NotificationProvider>
        <WindowManagerProvider>
          <OSShell />
        </WindowManagerProvider>
      </NotificationProvider>
    </OSThemeProvider>
  )
}
